import { Text, Button, Badge } from '@fluentui/react-components';
import { ArrowLeftRegular, FolderRegular, DocumentRegular, ChatRegular, ClipboardRegular, MoneyRegular } from '@fluentui/react-icons';
import { CafPhaseTracker } from '../components/CafPhaseTracker';
import type { Project } from '../services/project-service';
import type { MethodologyDocument } from '../services/document-service';

const STATUS_COLOURS: Record<string, 'warning' | 'success' | 'brand'> = {
  draft: 'warning',
  review: 'brand',
  final: 'success',
};

const PHASE_ORDER = ['governance', 'strategy', 'plan', 'ready', 'adopt', 'review', 'misc'];

interface ProjectDetailPageProps {
  project: Project;
  documents: MethodologyDocument[];
  onBack: () => void;
  onSelectDocument: (doc: MethodologyDocument) => void;
  onStartAssessment?: () => void;
  onOpenRegisters?: () => void;
  onOpenCostEstimate?: () => void;
}

export function ProjectDetailPage({ project, documents, onBack, onSelectDocument, onStartAssessment, onOpenRegisters, onOpenCostEstimate }: ProjectDetailPageProps) {
  // Group documents by CAF phase
  const docsByPhase = PHASE_ORDER
    .map(phase => ({
      phase,
      docs: documents.filter(d => d.cafPhase === phase),
    }))
    .filter(g => g.docs.length > 0);

  return (
    <div className="page-root page-root--scrollable project-detail-page">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title="Back to projects" />
        <FolderRegular className="icon-title" />
        <Text size={500} weight="semibold">{project.name}</Text>
        <div className="title-bar-actions">
          {onOpenRegisters && (
            <Button appearance="subtle" icon={<ClipboardRegular />} onClick={onOpenRegisters} size="small">
              Registers
            </Button>
          )}
          {onOpenCostEstimate && (
            <Button appearance="subtle" icon={<MoneyRegular />} onClick={onOpenCostEstimate} size="small">
              Cost Estimate
            </Button>
          )}
          {onStartAssessment && (
            <Button appearance="primary" icon={<ChatRegular />} onClick={onStartAssessment} size="small">
              CASE Assessment
            </Button>
          )}
          <Badge size="small" appearance="outline" color={project.status === 'complete' ? 'success' : 'brand'}>{project.status}</Badge>
        </div>
      </div>

      <CafPhaseTracker currentPhase={project.cafPhase} documents={documents} />

      <div className="project-meta">
        <div className="meta-grid">
          <div className="meta-item"><span className="text-muted">Customer</span><span>{project.customerId}</span></div>
          <div className="meta-item"><span className="text-muted">Scope</span><span>{project.scope}</span></div>
          <div className="meta-item"><span className="text-muted">Region</span><span>{project.azureRegion}</span></div>
          <div className="meta-item"><span className="text-muted">Hypervisor</span><span>{project.hypervisor}</span></div>
          <div className="meta-item"><span className="text-muted">Documents</span><span>{documents.length}</span></div>
        </div>
      </div>

      <div className="table-scroll-container">
        {docsByPhase.map(({ phase, docs }) => (
          <div key={phase} className="phase-group">
            <Text size={400} weight="semibold" className="phase-group-title">
              {phase.charAt(0).toUpperCase() + phase.slice(1)}
              <Badge size="small" appearance="outline" className="phase-count">{docs.length}</Badge>
            </Text>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Template</th>
                  <th>Status</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => (
                  <tr key={doc.id} className="clickable" onClick={() => onSelectDocument(doc)}>
                    <td>
                      <div className="flex-row">
                        <DocumentRegular />
                        <Text weight="semibold">{doc.title}</Text>
                      </div>
                    </td>
                    <td className="text-muted">{doc.templateId}</td>
                    <td><Badge size="small" color={STATUS_COLOURS[doc.status] ?? 'warning'}>{doc.status}</Badge></td>
                    <td className="text-muted">v{doc.version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        {documents.length === 0 && (
          <div className="table-empty">
            <Text className="text-muted">No documents yet. Scaffold documents from the project settings.</Text>
          </div>
        )}
      </div>

      {/* Snapshots Timeline */}
      {project.snapshots && project.snapshots.length > 0 && (
        <div style={{ padding: '0 24px 24px' }}>
          <Text size={400} weight="semibold" style={{ display: 'block', marginBottom: 12 }}>
            Engagement Snapshots
            <Badge size="small" appearance="outline" style={{ marginLeft: 8 }}>{project.snapshots.length}</Badge>
          </Text>
          <table className="data-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Label</th>
                <th>Date</th>
                <th>Workloads</th>
                <th>Facts</th>
                <th>Risks</th>
                <th>Est. Monthly</th>
              </tr>
            </thead>
            <tbody>
              {[...project.snapshots].reverse().map(s => (
                <tr key={s.version}>
                  <td><Badge size="small" appearance="outline">v{s.version}</Badge></td>
                  <td>{s.label}</td>
                  <td className="text-muted">{new Date(s.timestamp).toLocaleDateString()}</td>
                  <td>{s.workloadCount}</td>
                  <td>{s.factCount}</td>
                  <td>{s.riskCount}</td>
                  <td>{s.grandTotalMonthly ? `$${s.grandTotalMonthly.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
